/**
 * GlobalFamilyTrees — /global-family-trees
 *
 * Public "pick your country" landing that invites diaspora families
 * worldwide to build trees & host memorials on Social Pulse. India
 * links out to indianfamilytree.com; every other country enters the
 * Heritage flow scoped to that country.
 */
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Feather, Globe2, ArrowRight, TreePine, Flower2, MapPin, ArrowLeft } from "lucide-react";

const P = {
  bg:        "#FBF5EA",
  ink:       "#2A1D10",
  inkSoft:   "#5C4A32",
  parchment: "#F0E4CE",
  saffron:   "#B25D38",
  saffronSoft:"#F6DCC5",
  green:     "#0F6B45",
  rose:      "#8B3A3A",
};

// External sibling site URL (previewed at legacy-archive-13.emergent.host).
const INDIA_URL = "https://legacy-archive-13.emergent.host";

const COUNTRIES = [
  { code: "IN", name: "India",         external: INDIA_URL,               emoji: "🇮🇳", note: "IndianFamilyTree.com" },
  { code: "US", name: "United States", to: "/heritage?country=us",        emoji: "🇺🇸" },
  { code: "GB", name: "United Kingdom",to: "/heritage?country=gb",        emoji: "🇬🇧" },
  { code: "CA", name: "Canada",        to: "/heritage?country=ca",        emoji: "🇨🇦" },
  { code: "AU", name: "Australia",     to: "/heritage?country=au",        emoji: "🇦🇺" },
  { code: "AE", name: "UAE",           to: "/heritage?country=ae",        emoji: "🇦🇪" },
  { code: "SG", name: "Singapore",     to: "/heritage?country=sg",        emoji: "🇸🇬" },
  { code: "IE", name: "Ireland",       to: "/heritage?country=ie",        emoji: "🇮🇪" },
  { code: "DE", name: "Germany",       to: "/heritage?country=de",        emoji: "🇩🇪" },
  { code: "FR", name: "France",        to: "/heritage?country=fr",        emoji: "🇫🇷" },
  { code: "NL", name: "Netherlands",   to: "/heritage?country=nl",        emoji: "🇳🇱" },
  { code: "IT", name: "Italy",         to: "/heritage?country=it",        emoji: "🇮🇹" },
  { code: "ES", name: "Spain",         to: "/heritage?country=es",        emoji: "🇪🇸" },
  { code: "ZA", name: "South Africa",  to: "/heritage?country=za",        emoji: "🇿🇦" },
  { code: "KE", name: "Kenya",         to: "/heritage?country=ke",        emoji: "🇰🇪" },
  { code: "NG", name: "Nigeria",       to: "/heritage?country=ng",        emoji: "🇳🇬" },
  { code: "BR", name: "Brazil",        to: "/heritage?country=br",        emoji: "🇧🇷" },
  { code: "MX", name: "Mexico",        to: "/heritage?country=mx",        emoji: "🇲🇽" },
  { code: "PH", name: "Philippines",   to: "/heritage?country=ph",        emoji: "🇵🇭" },
  { code: "MY", name: "Malaysia",      to: "/heritage?country=my",        emoji: "🇲🇾" },
  { code: "LK", name: "Sri Lanka",     to: "/heritage?country=lk",        emoji: "🇱🇰" },
  { code: "NP", name: "Nepal",         to: "/heritage?country=np",        emoji: "🇳🇵" },
  { code: "BD", name: "Bangladesh",    to: "/heritage?country=bd",        emoji: "🇧🇩" },
  { code: "PK", name: "Pakistan",      to: "/heritage?country=pk",        emoji: "🇵🇰" },
];

export default function GlobalFamilyTrees() {
  return (
    <div style={{ background: P.bg, color: P.ink, minHeight: "100vh" }} data-testid="global-family-trees">
      <Helmet>
        <title>Global Family Trees — pick your country · Social Pulse</title>
        <meta name="description"
              content="Build your family tree and host memorials on the Social Pulse Heritage network. Pick your country to enter the regional archive." />
      </Helmet>

      {/* Thin nav */}
      <header
        className="sticky top-0 z-30 border-b backdrop-blur"
        style={{ background: `${P.bg}EE`, borderColor: P.saffronSoft }}
        data-testid="global-thin-nav"
      >
        <div className="max-w-6xl mx-auto px-4 h-11 flex items-center justify-between text-sm">
          <Link to="/heritage" className="flex items-center gap-1.5 opacity-80 hover:opacity-100"
                data-testid="global-back">
            <ArrowLeft className="w-4 h-4" /> Heritage
          </Link>
          <span className="flex items-center gap-1.5 font-display font-bold" style={{ color: P.ink }}>
            <Globe2 className="w-4 h-4" style={{ color: P.saffron }} /> Global Family Trees
          </span>
          <Link to="/" className="opacity-70 hover:opacity-100 text-xs" data-testid="global-nav-sp">
            <span className="opacity-60">↗</span> Social Pulse
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section
        className="border-b"
        style={{ background: `linear-gradient(180deg, ${P.parchment} 0%, ${P.bg} 100%)`,
                 borderColor: P.saffronSoft }}
      >
        <div className="max-w-4xl mx-auto px-4 py-14 text-center">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4"
               style={{ background: "#FFF3E0", color: P.saffron }}>
            <Feather className="w-3.5 h-3.5" />
            <span className="text-[11px] font-mono uppercase tracking-wider">
              A network of country archives
            </span>
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl leading-[1.05]">
            Where does your <span style={{ color: P.saffron }}>family story</span> begin?
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-base sm:text-lg" style={{ color: P.inkSoft }}>
            Pick your country to enter the regional Heritage archive. Each
            country hosts its own family trees, memorials, and community
            traditions — connected to the global Social Pulse network.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/family-trees/new"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-white font-semibold"
                  style={{ background: P.saffron }}
                  data-testid="global-cta-tree">
              <TreePine className="w-4 h-4" /> Start a family tree
            </Link>
            <Link to="/celebrate/submit"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-white font-semibold"
                  style={{ background: P.rose }}
                  data-testid="global-cta-memorial">
              <Flower2 className="w-4 h-4" /> Create a memorial
            </Link>
          </div>
        </div>
      </section>

      {/* Country grid */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-4 h-4" style={{ color: P.green }} />
          <h2 className="font-display text-2xl">Pick a country</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {COUNTRIES.map((c) => {
            const inner = (
              <>
                <span className="text-xl" aria-hidden>{c.emoji}</span>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{c.name}</div>
                  {c.note && (
                    <div className="text-[11px] opacity-60 truncate">{c.note}</div>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 ml-auto opacity-40" />
              </>
            );
            const commonProps = {
              className: "rounded-xl border bg-white p-3 flex items-center gap-2 hover:shadow-md transition",
              style:     { borderColor: P.saffronSoft },
              "data-testid": `global-country-${c.code}`,
            };
            return c.external ? (
              <a key={c.code} href={c.external} target="_blank" rel="noreferrer" {...commonProps}>
                {inner}
              </a>
            ) : (
              <Link key={c.code} to={c.to} {...commonProps}>
                {inner}
              </Link>
            );
          })}
        </div>
        <p className="mt-6 text-xs opacity-60 text-center">
          Don't see your country? Start a family tree anyway — every country
          is welcome, and we'll spin up the regional archive as families arrive.
        </p>
      </section>
    </div>
  );
}
